import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { masterclassService } from '../../services/masterclassService'
import { useAuthStore } from '../../store/authStore'
import { CheckCircle2, AlertCircle, DollarSign, X, Loader2, ShieldAlert, ArrowLeft } from 'lucide-react'

export default function EventCheckInPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const passToken = searchParams.get('pass')
  const regNumber = searchParams.get('reg')

  const [loading, setLoading] = useState(true)
  const [passData, setPassData] = useState(null)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [actionResult, setActionResult] = useState(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [showOverrideForm, setShowOverrideForm] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [txRef, setTxRef] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated) return
    if (fetchedRef.current) return
    if (!passToken && !regNumber) {
      setError('No pass token found in the QR code. Please scan a valid Digital Event Pass QR code.')
      setLoading(false)
      return
    }
    fetchedRef.current = true
    loadPassInfo()
  }, [isAuthenticated, passToken, regNumber])

  async function loadPassInfo() {
    setLoading(true)
    setError('')
    try {
      if (passToken) {
        const data = await masterclassService.getPassInfo(passToken)
        setPassData(data)
      } else if (regNumber) {
        // Fallback: verify by registration number, then construct pass data
        const data = await masterclassService.verifyRegistration(regNumber)
        const reg = data.registration
        const isFree = reg.entitlement_type === 'FREE'
        const isPaid = reg.payment_status === 'COMPLETED'
        const fee = data.fee_amount || (reg.entitlement_type === 'PAYMENT_REQUIRED' ? 150000 : 0)
        setPassData({
          registration: {
            registration_number: reg.registration_number,
            first_name: reg.first_name,
            last_name: reg.last_name,
            entitlement_type: reg.entitlement_type,
            payment_status: reg.payment_status,
            pass_type: isFree ? 'SMA Student (Complimentary)' : 'Non-Student Guest (UGX 150,000)',
            checked_in: reg.checked_in,
            checked_in_at: null,
            checked_in_by: null,
          },
          masterclass: reg.masterclass,
          payment: {
            total_fee: fee,
            amount_paid: isFree ? fee : (isPaid ? fee : 0),
            outstanding_balance: isFree ? 0 : (isPaid ? 0 : fee),
            currency: 'UGX',
            is_free: isFree,
            is_paid: isPaid,
            payment_required: !isFree && !isPaid,
          },
          access_status: data.access_status,
          can_checkin: data.can_checkin,
        })
      }
    } catch (err) {
      setError(err.message || 'Failed to verify pass. The QR code may be invalid, expired, or revoked.')
    } finally {
      setLoading(false)
    }
  }

  async function handleCheckIn(options = {}) {
    setActionLoading(true)
    setError('')
    try {
      let result
      if (passToken) {
        result = await masterclassService.checkInByPass(passToken, options)
      } else if (regNumber) {
        result = await masterclassService.adminCheckIn(regNumber, options)
      }
      setActionResult(result)
      setShowPaymentForm(false)
      setShowOverrideForm(false)
      // Reload pass info to reflect updated state
      if (passToken) {
        const fresh = await masterclassService.getPassInfo(passToken)
        setPassData(fresh)
      }
    } catch (err) {
      setError(err.message || 'Check-in failed')
    } finally {
      setActionLoading(false)
    }
  }

  function formatCurrency(amount) {
    return `UGX ${Number(amount).toLocaleString('en-UG')}`
  }

  function formatDateTime(dateStr, timeStr) {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    const date = d.toLocaleDateString('en-UG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    const time = timeStr || ''
    return time ? `${date} at ${time}` : date
  }

  function formatTimestamp(ts) {
    if (!ts) return '—'
    return new Date(ts).toLocaleString('en-UG', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Not authenticated — prompt login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <ShieldAlert className="w-12 h-12 text-gray-900 dark:text-white mx-auto" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Login Required</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            You need to be logged in as an SMA staff member to verify and check in attendees.
          </p>
          <button
            onClick={() => navigate(`/login?redirect=/event-check-in?pass=${encodeURIComponent(passToken || '')}`)}
            className="px-6 py-3 bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Log in to continue
          </button>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 mx-auto animate-spin mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Verifying pass…</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !passData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Pass Verification Failed</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{error}</p>
          <button
            onClick={() => navigate('/masterclass/admin')}
            className="px-6 py-3 bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Back to Admin
          </button>
        </div>
      </div>
    )
  }

  const reg = passData?.registration
  const mc = passData?.masterclass
  const pay = passData?.payment
  const isAlreadyCheckedIn = reg?.checked_in === true
  const needsPayment = pay?.payment_required === true

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black py-6 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="text-2xl sm:text-3xl text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}>
              Event Check-In
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Scanned by {user?.first_name || ''} {user?.last_name || ''} ({user?.user_type})
            </p>
          </div>
          <button
            onClick={() => navigate('/masterclass/admin')}
            className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 500 }}>Admin</span>
          </button>
        </div>

        {/* Already checked in banner */}
        {isAlreadyCheckedIn && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-500/10 border-l-4 border-green-500">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 text-sm font-semibold mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>
              <CheckCircle2 className="w-5 h-5" />
              Already Checked In
            </div>
            <p className="text-xs text-green-600 dark:text-green-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {reg.checked_in_at && `Checked in at ${formatTimestamp(reg.checked_in_at)}`}
              {reg.checked_in_by?.name && ` by ${reg.checked_in_by.name}`}
            </p>
          </div>
        )}

        {/* Attendee info */}
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
          {/* Name + Reg ID */}
          <div className="mb-4 pb-4 border-b border-gray-100 dark:border-white/5">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {reg?.first_name} {reg?.last_name}
            </h2>
            <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">{reg?.registration_number}</p>
          </div>

          {/* Event details */}
          <div className="space-y-3 mb-4 pb-4 border-b border-gray-100 dark:border-white/5">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Event</div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{mc?.topic}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Date & Time</div>
                <div className="text-xs font-medium text-gray-900 dark:text-white">{formatDateTime(mc?.event_date, mc?.start_time)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Venue</div>
                <div className="text-xs font-medium text-gray-900 dark:text-white">{mc?.venue || '—'}</div>
              </div>
            </div>
          </div>

          {/* Pass type + Payment summary */}
          <div className="space-y-3">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Pass Type</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">{reg?.pass_type}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Total Fee</div>
                <div className="text-xs font-semibold text-gray-900 dark:text-white">{formatCurrency(pay?.total_fee || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Amount Paid</div>
                <div className="text-xs font-semibold text-green-600 dark:text-green-400">{formatCurrency(pay?.amount_paid || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Outstanding</div>
                <div className={`text-xs font-semibold ${(pay?.outstanding_balance || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {formatCurrency(pay?.outstanding_balance || 0)}
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Payment Status</div>
              <div className="text-sm font-medium">
                {pay?.is_free ? (
                  <span className="text-green-600 dark:text-green-400">Free / Not Required</span>
                ) : pay?.is_paid ? (
                  <span className="text-green-600 dark:text-green-400">Paid</span>
                ) : (
                  <span className="text-red-600 dark:text-red-400">Pending</span>
                )}
              </div>
            </div>
            {isAlreadyCheckedIn && (
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-0.5">Check-in Status</div>
                <div className="text-sm font-medium text-green-600 dark:text-green-400">
                  Checked in{reg.checked_in_at ? ` at ${formatTimestamp(reg.checked_in_at)}` : ''}
                  {reg.checked_in_by?.name ? ` by ${reg.checked_in_by.name}` : ''}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action area */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border-l-4 border-red-500 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {actionResult?.success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-500/10 border-l-4 border-green-500">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 text-sm font-semibold mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>
              <CheckCircle2 className="w-5 h-5" />
              {actionResult.already_checked_in ? 'Already checked in' : 'Check-in successful'}
            </div>
            <p className="text-xs text-green-600 dark:text-green-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {actionResult.registration?.first_name} {actionResult.registration?.last_name} has been checked in.
              {actionResult.certificate?.certificate_number && ` Certificate: ${actionResult.certificate.certificate_number}`}
            </p>
          </div>
        )}

        {/* Fully paid or free: show confirm check-in */}
        {!isAlreadyCheckedIn && !needsPayment && !actionResult?.success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-500/10 border-l-4 border-green-500">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 text-sm font-semibold mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>
              <CheckCircle2 className="w-5 h-5" />
              Payment verified — ready for check-in.
            </div>
            <button
              onClick={() => handleCheckIn()}
              disabled={actionLoading}
              className="w-full py-3.5 bg-green-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              Confirm Check-In
            </button>
          </div>
        )}

        {/* Outstanding balance: show warning + two actions */}
        {!isAlreadyCheckedIn && needsPayment && !actionResult?.success && !showPaymentForm && !showOverrideForm && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-500/10 border-l-4 border-red-500">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm font-semibold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>
              <AlertCircle className="w-5 h-5" />
              Outstanding balance: {formatCurrency(pay?.outstanding_balance || 150000)}. Payment is required before entry.
            </div>
            <div className="space-y-2 mt-3">
              <button
                onClick={() => setShowPaymentForm(true)}
                disabled={actionLoading}
                className="w-full py-3.5 bg-primary-500 text-white text-sm font-bold uppercase tracking-wider hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <DollarSign className="w-5 h-5" />
                Receive Payment & Check In
              </button>
              <button
                onClick={() => navigate('/masterclass/admin')}
                disabled={actionLoading}
                className="w-full py-3.5 bg-transparent text-gray-600 dark:text-gray-400 text-sm font-bold uppercase tracking-wider border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" />
                Cancel Check-In
              </button>
              {user?.user_type === 'admin' && (
                <button
                  onClick={() => setShowOverrideForm(true)}
                  disabled={actionLoading}
                  className="w-full py-2.5 text-xs font-medium text-amber-600 dark:text-amber-500 hover:text-amber-700 dark:hover:text-amber-400 transition-colors"
                >
                  Admin override (skip payment)
                </button>
              )}
            </div>
          </div>
        )}

        {/* Payment collection form */}
        {showPaymentForm && !actionResult?.success && (
          <div className="mb-4 p-4 surface border-t-4 border-primary-500">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Collect Door Payment</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium block mb-1.5">Amount</label>
                <div className="px-3 py-2.5 bg-gray-100 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(pay?.outstanding_balance || 150000)}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium block mb-1.5">Payment Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {['cash', 'mobile_money', 'bank_transfer'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setPaymentMethod(m)}
                      className={`p-2 text-center text-xs uppercase tracking-wider transition-all ${
                        paymentMethod === m
                          ? 'bg-primary-500 text-white font-bold'
                          : 'bg-gray-100 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {m.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium block mb-1.5">Transaction Reference</label>
                <input
                  type="text"
                  value={txRef}
                  onChange={(e) => setTxRef(e.target.value)}
                  placeholder="Receipt # / Transaction ID (optional)"
                  className="w-full px-3 py-2.5 bg-transparent border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary-500"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => handleCheckIn({ record_payment: true, payment_method: paymentMethod, transaction_reference: txRef })}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-green-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  Collect & Check In
                </button>
                <button
                  onClick={() => setShowPaymentForm(false)}
                  disabled={actionLoading}
                  className="px-4 py-3 bg-transparent text-gray-600 dark:text-gray-400 text-sm font-bold uppercase tracking-wider border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Override form (admin only) */}
        {showOverrideForm && !actionResult?.success && (
          <div className="mb-4 p-4 surface border-l-4 border-amber-500">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500 text-sm font-bold mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>
              <ShieldAlert className="w-5 h-5" />
              Payment Override (Admin Only)
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              This will check in the attendee without recording payment. The override and reason will be stored in the audit trail.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium block mb-1.5">Override Reason (required)</label>
                <textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="e.g. VIP guest, speaker waiver, complimentary pass approved by director…"
                  rows={3}
                  className="w-full px-3 py-2.5 bg-transparent border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-amber-500 resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleCheckIn({ override: true, override_reason: overrideReason })}
                  disabled={actionLoading || !overrideReason.trim()}
                  className="flex-1 py-3 bg-amber-500 text-white text-sm font-bold uppercase tracking-wider hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldAlert className="w-5 h-5" />}
                  Override & Check In
                </button>
                <button
                  onClick={() => setShowOverrideForm(false)}
                  disabled={actionLoading}
                  className="px-4 py-3 bg-transparent text-gray-600 dark:text-gray-400 text-sm font-bold uppercase tracking-wider border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Done / scan next */}
        {actionResult?.success && (
          <button
            onClick={() => {
              setActionResult(null)
              setPassData(null)
              setError('')
              fetchedRef.current = false
              navigate('/masterclass/admin')
            }}
            className="w-full py-3.5 bg-primary-500 text-white text-sm font-bold uppercase tracking-wider hover:bg-primary-600 transition-colors"
          >
            Done — Back to Admin
          </button>
        )}
      </div>
    </div>
  )
}
